import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { hasFileChanges } from '@risksmart-app/components/src/file/fileUtils';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { ownerAndContributorIds } from 'src/components/form';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import { getContributors, getOwners } from 'src/rbac/contributorHelper';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useUpdateAsset } from '@/hooks/mutations/asset';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useGetAssetById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import AssetForm from '../../forms/AssetForm';
import type { AssetFormFieldData } from '../../forms/assetSchema';
import { defaultValues } from '../../forms/assetSchema';

const Tab: FC = () => {
  useI18NSummaryHelpContent('assets.help');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const { updateFiles } = useFileUpdate();
  const navigate = useNavigate();
  const assetId = useGetGuidParam('assetId');
  const { data, error } = useGetAssetById({ queryArgs: { id: assetId } });
  if (error) {
    throw error;
  }

  if (data?.asset.length === 0) {
    throw new PageNotFound(`Asset with id ${assetId} not found`);
  }
  const asset = data?.asset[0];
  const { hasPermission: canEditAsset, loading: isLoadingEditAsset } =
    useHasPermissionQuery('update:asset', asset);
  const values = {
    ...defaultValues,
    ...asset,
    files: asset?.files.map((rf) => rf.file),
    Owners: getOwners(asset),
    Contributors: getContributors(asset),
    ancestorContributors: asset?.ancestorContributors ?? [],
    Assignee: asset?.Assignee
      ? { type: 'user' as const, value: asset.Assignee }
      : null,
  };
  const { updateAsset } = useUpdateAsset();

  const onDismiss = () => {
    navigate(-1);
  };

  const onSave = async (
    data: AssetFormFieldData,
    confirmChangeRequest?: boolean
  ) => {
    const { files } = data;

    if (!asset) {
      throw new Error('asset missing');
    }

    const ids = ownerAndContributorIds(data);

    await updateAsset(
      {
        Title: data.Title,
        Status: data.Status,
        Description: data.Description ?? null,
        AssetType: data.AssetType ?? null,
        SerialNumber: data.SerialNumber ?? null,
        PurchaseDate: data.PurchaseDate ?? null,
        Location: data.Location ?? null,
        Cost: data.Cost ?? null,
        DepreciationMethod: data.DepreciationMethod ?? null,
        Assignee: data.Assignee?.value ?? null,
        OriginalTimestamp: asset.ModifiedAtTimestamp,
        Id: asset.Id,
        CustomAttributeData: data.CustomAttributeData || undefined,
        DepartmentTypeIds:
          data.departments?.map((d) => d.DepartmentTypeId) || [],
        TagTypeIds: data.tags?.map((t) => t.TagTypeId) || [],
        ...ids,
      },
      {
        confirmChangeRequest,
        hasFileChanges: hasFileChanges(
          values?.files?.map((file) => ({ Id: file?.Id })),
          files
        ),
      }
    );
    await updateFiles({
      parentType: Parent_Type_Enum.Asset,
      parentId: asset?.Id,
      originalFiles: values?.files,
      selectedFiles: files,
    });
  };

  return (
    <AssetForm
      assetId={assetId}
      values={values as AssetFormFieldData}
      onDismiss={onDismiss}
      onSave={onSave}
      readOnly={isLoadingEditAsset || !canEditAsset}
      publishToolbarState={objectToolbarEnabled}
    />
  );
};

export default Tab;
